import { useEffect, useRef, useState } from "react";
import { UserConfigDto } from "@lib/ShiOneClient";
import { ImpersonationState } from "@features/impersonation-feature/types";
import {
  useGetUserConfig,
  useUpsertUserConfig,
} from "../../../../../modules/hooks/useUserConfig";

const PIN_USER_PREF_KEY = "ImpersonationPinCollapsed";
const PIN_USER_PREF_PERSIST_DELAY_MS = 1500;
const PIN_USER_PREF_DEFAULT_COLLAPSED: boolean = false;

export function useImpersonationState() {
  const [impersonationState, setImpersonationState] =
    useState<ImpersonationState>({
      current: null,
      pending: null,
      userByAccountId: {},
      collapsed: PIN_USER_PREF_DEFAULT_COLLAPSED,
    });
  const { userConfig: pinUserPref } = useGetUserConfig(PIN_USER_PREF_KEY);
  const { mutate: upsertUserConfig } = useUpsertUserConfig();
  const pinUserPrefTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingPinUserPrefRef = useRef<boolean | null>(null);
  const isPinToggleRef = useRef(false);

  const persistCollapse = (collapsed: boolean) => {
    const config = new UserConfigDto();
    config.settingsKey = PIN_USER_PREF_KEY;
    config.settingsValue = String(collapsed);
    upsertUserConfig(config);
    pendingPinUserPrefRef.current = null;
  };

  useEffect(() => {
    const savedValue = pinUserPref?.settingsValue;
    if (
      !isPinToggleRef.current &&
      (savedValue === "true" || savedValue === "false")
    ) {
      setImpersonationState((prev) => ({
        ...prev,
        collapsed: savedValue === "true",
      }));
    }
  }, [pinUserPref]);

  useEffect(() => {
    return () => {
      if (pinUserPrefTimeoutRef.current) {
        clearTimeout(pinUserPrefTimeoutRef.current);
      }
    };
  }, []);

  const setImpersonationCollapse = (value: boolean) => {
    if (pinUserPrefTimeoutRef.current) {
      clearTimeout(pinUserPrefTimeoutRef.current);
    }

    isPinToggleRef.current = true;
    pendingPinUserPrefRef.current = value;
    setImpersonationState((prev) => ({ ...prev, collapsed: value }));

    pinUserPrefTimeoutRef.current = setTimeout(() => {
      persistCollapse(value);
      pinUserPrefTimeoutRef.current = null;
    }, PIN_USER_PREF_PERSIST_DELAY_MS);
  };

  const cancelImpersonationCollapsePersistence = () => {
    if (pinUserPrefTimeoutRef.current) {
      clearTimeout(pinUserPrefTimeoutRef.current);
      pinUserPrefTimeoutRef.current = null;
    }
    pendingPinUserPrefRef.current = null;
  };

  return {
    impersonationState,
    setImpersonationState,
    setImpersonationCollapse,
    cancelImpersonationCollapsePersistence,
  };
}
